import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Mail, CheckCircle2, ArrowLeft, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const id = language === "id";
  const token = searchParams.get("token");

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState("");
  const [points, setPoints] = useState(0);

  React.useEffect(() => {
    if (token) {
      setStatus("loading");
      axios.get(`${API}/auth/verify-email?token=${token}`)
        .then(r => {
          setStatus("success");
          setPoints(r.data.points_earned || 50);
          setMessage(r.data.message || "");
        })
        .catch(err => {
          setStatus("error");
          setMessage(err.response?.data?.detail || "Invalid or expired link");
        });
    }
  }, [token]);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      toast.success(id ? "Email verifikasi dikirim ulang!" : "Verification email resent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || (id ? "Gagal mengirim ulang" : "Failed to resend"));
    }
    setResending(false);
  };

  if (!token) {
    // Arrived without token — show resend page for logged-in users
    return (
      <ImmersiveLayout>
        <section className="min-h-screen flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="max-w-md w-full text-center"
          >
            <div className="w-16 h-16 rounded-full bg-seikat-pink/15 flex items-center justify-center mx-auto mb-6">
              <Mail size={28} className="text-seikat-pink" />
            </div>
            <h1 className="font-heading text-4xl text-seikat-bone mb-4">
              {id ? <>Verifikasi <em>Email.</em></> : <>Verify <em>Email.</em></>}
            </h1>
            <p className="font-body text-seikat-bone/60 leading-relaxed mb-8">
              {id
                ? "Cek inbox kamu untuk link verifikasi. Tidak menerimanya?"
                : "Check your inbox for a verification link. Didn't receive it?"}
            </p>
            {user && (
              <button onClick={handleResend} disabled={resending} className="btn-pill btn-pill--solid" data-testid="resend-verification-btn">
                {resending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {id ? "Kirim Ulang" : "Resend"}
              </button>
            )}
            <div className="mt-6">
              <Link to="/home" className="font-body text-seikat-bone-muted text-sm hover:text-seikat-pink transition-colors">
                {id ? "Lewati untuk sekarang" : "Skip for now"}
              </Link>
            </div>
          </motion.div>
        </section>
      </ImmersiveLayout>
    );
  }

  return (
    <ImmersiveLayout>
      <section className="min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-md w-full text-center"
        >
          <AnimatePresence mode="wait">
            {status === "loading" && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Loader2 size={40} className="animate-spin text-seikat-pink mx-auto mb-6" />
                <p className="font-body text-seikat-bone/60">
                  {id ? "Memverifikasi..." : "Verifying..."}
                </p>
              </motion.div>
            )}
            {status === "success" && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="w-20 h-20 rounded-full bg-[#C5DB7B]/15 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={36} className="text-[#C5DB7B]" />
                </div>
                <h1 className="font-heading text-4xl text-seikat-bone mb-4">
                  {id ? <>Email <em>terverifikasi!</em></> : <>Email <em>verified!</em></>}
                </h1>
                <p className="font-body text-seikat-bone/60 mb-4">
                  {id
                    ? `Selamat! Kamu mendapatkan ${points} poin loyalitas.`
                    : `Congratulations! You've earned ${points} loyalty points.`}
                </p>
                <div className="glass-dark rounded-2xl p-5 mb-8 inline-block">
                  <p className="font-heading text-5xl text-[#C5DB7B]">+{points}</p>
                  <p className="font-body text-xs tracking-widest uppercase text-seikat-bone-muted mt-1">
                    {id ? "Poin Loyalitas" : "Loyalty Points"}
                  </p>
                </div>
                <button onClick={() => navigate("/home")} className="btn-pill btn-pill--solid w-full justify-center" data-testid="verify-continue-btn">
                  {id ? "Mulai Belanja" : "Start Shopping"}
                </button>
              </motion.div>
            )}
            {status === "error" && (
              <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle size={36} className="text-red-400" />
                </div>
                <h1 className="font-heading text-4xl text-seikat-bone mb-4">
                  {id ? <>Link <em>tidak valid.</em></> : <>Link <em>invalid.</em></>}
                </h1>
                <p className="font-body text-seikat-bone/60 mb-8">{message}</p>
                {user && (
                  <button onClick={handleResend} disabled={resending} className="btn-pill btn-pill--solid">
                    {resending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {id ? "Kirim Ulang Link" : "Resend Link"}
                  </button>
                )}
                <div className="mt-4">
                  <Link to="/home" className="font-body text-seikat-bone-muted text-sm hover:text-seikat-pink transition-colors">
                    {id ? "Kembali ke Beranda" : "Back to Home"}
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>
    </ImmersiveLayout>
  );
}
